// src/components/settings/shared/ManagementSection.jsx
import React, { useState } from 'react';
import { Users, Building2, ClipboardList, MapPin, FileText, Package, Database, Truck } from 'lucide-react';
import { TabGroup, Toggle, SelectInput, FormField } from './FormComponents';

// Management features configuration
const MANAGEMENT_FEATURES = {
  team: {
    label: 'Team Management',
    description: 'Manage team members, roles, and organizational structure',
    icon: Users,
    availableFor: ['organisation', 'institution', 'education', 'catalog', 'booking', 'general']
  },
  assets: {
    label: 'Asset Management', 
    description: 'Track facilities, equipment, vehicles, and maintenance',
    icon: Building2,
    availableFor: ['organisation', 'institution', 'catalog', 'booking', 'general']
  },
  tasks: {
    label: 'Task Management',
    description: 'Organize projects, assignments, and task workflows',
    icon: ClipboardList,
    availableFor: ['organisation', 'institution', 'education', 'general']
  },
  sites: {
    label: 'Site Management',
    description: 'Manage multiple locations, branches, or venues',
    icon: MapPin,
    availableFor: ['organisation', 'institution', 'booking', 'general']
  },
  clients: {
    label: 'Client Management',
    description: 'Manage customers, members, students, or constituents',
    icon: Users,
    availableFor: ['organisation', 'institution', 'catalog', 'booking', 'general']
  },
  documents: {
    label: 'Document Management',
    description: 'Organize files, policies, and document workflows',
    icon: FileText,
    availableFor: ['organisation', 'institution', 'education', 'general']
  },
  resources: {
    label: 'Resources Management',
    description: 'Track and allocate distributable resources across sites',
    icon: Package,
    availableFor: ['organisation', 'institution', 'catalog', 'booking', 'general']
  },
  records: {
    label: 'Records Management',
    description: 'Comprehensive activity tracking and organizational audit trail',
    icon: Database,
    availableFor: ['organisation', 'institution', 'education', 'catalog', 'booking', 'general']
  },
  logistics: {
    label: 'Logistics Management',
    description: 'Handle shipping, delivery, and supply chain operations',
    icon: Truck,
    availableFor: ['organisation', 'catalog', 'booking', 'general']
  },
  contracts: {
    label: 'Contract & SLA Management',
    description: 'Manage agreements, contracts, and service level agreements',
    icon: FileText,
    availableFor: ['organisation', 'institution', 'catalog', 'booking', 'general']
  }
};

const getAvailableManagementFeatures = (templateType) => {
  return Object.keys(MANAGEMENT_FEATURES).filter(feature => 
    MANAGEMENT_FEATURES[feature].availableFor.includes(templateType)
  );
};

export const ManagementSection = ({ formData, onInputChange, templateType }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  const availableFeatures = getAvailableManagementFeatures(templateType);
  const enabledFeatures = Object.keys(formData.managementFeatures || {}).filter(
    key => formData.managementFeatures[key]?.enabled
  );

  const subTabs = [
    { id: 'overview', label: 'Overview' },
    ...enabledFeatures.map(feature => ({
      id: feature,
      label: MANAGEMENT_FEATURES[feature].label
    }))
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Management Features</h3>
        <p className="text-gray-600">
          Enable additional management capabilities for your {templateType}
        </p>
      </div>

      {/* Sub-navigation */}
      <TabGroup 
        tabs={subTabs}
        activeTab={activeSubTab}
        onChange={setActiveSubTab}
      />

      {/* Overview - Feature selection */}
      {activeSubTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {availableFeatures.map(feature => {
            const config = MANAGEMENT_FEATURES[feature];
            const isEnabled = formData.managementFeatures?.[feature]?.enabled;
            
            return (
              <div 
                key={feature} 
                className={`border rounded-xl p-6 transition-all ${
                  isEnabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <config.icon className={`h-8 w-8 ${isEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
                  <Toggle 
                    checked={isEnabled}
                    onChange={(value) => onInputChange(`managementFeatures.${feature}.enabled`, value)}
                  />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{config.label}</h4>
                <p className="text-sm text-gray-600 mb-4">{config.description}</p>
                
                {/* Quick configuration for enabled features */}
                {isEnabled && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                    <FormField label="Access Level">
                      <SelectInput
                        value={formData.managementFeatures?.[feature]?.accessLevel || 'internal'}
                        onChange={(value) => onInputChange(`managementFeatures.${feature}.accessLevel`, value)}
                        options={[
                          { value: 'public', label: 'Public' },
                          { value: 'members', label: 'Members Only' },
                          { value: 'internal', label: 'Internal Only' }
                        ]}
                      />
                    </FormField>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Individual feature configuration */}
      {enabledFeatures.map(feature => (
        activeSubTab === feature && (
          <ManagementFeatureConfig
            key={feature}
            featureType={feature}
            config={MANAGEMENT_FEATURES[feature]}
            formData={formData.managementFeatures[feature] || {}}
            onInputChange={(path, value) => onInputChange(`managementFeatures.${feature}.${path}`, value)}
            templateType={templateType}
          />
        )
      ))}

      {/* Global Management Settings */}
      {enabledFeatures.length > 0 && activeSubTab === 'overview' && (
        <div className="border border-gray-200 rounded-lg p-6">
          <h5 className="font-semibold text-gray-900 mb-4">Global Management Settings</h5>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Default Admin Email">
              <input
                type="email"
                value={formData.managementAdminEmail || ''}
                onChange={(e) => onInputChange('managementAdminEmail', e.target.value)}
                placeholder="admin@organisation.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </FormField>

            <FormField label="Management Access Level">
              <SelectInput
                value={formData.managementAccessLevel || 'internal'}
                onChange={(value) => onInputChange('managementAccessLevel', value)}
                options={[
                  { value: 'internal', label: 'Internal Only' },
                  { value: 'members', label: 'Members & Staff' },
                  { value: 'restricted', label: 'Restricted Access' }
                ]}
              />
            </FormField>
          </div>

          <div className="mt-4 space-y-3">
            <Toggle
              checked={formData.enableManagementNotifications || false}
              onChange={(value) => onInputChange('enableManagementNotifications', value)}
              label="Enable Management Notifications"
              description="Send notifications for management activities and updates"
            />
            
            <Toggle
              checked={formData.enableCrossModuleIntegration || true}
              onChange={(value) => onInputChange('enableCrossModuleIntegration', value)}
              label="Enable Cross-Module Integration"
              description="Allow data sharing between management modules (e.g., allocate resources to team members)"
            />
            
            <Toggle
              checked={formData.enableActivityTracking || true}
              onChange={(value) => onInputChange('enableActivityTracking', value)}
              label="Enable Activity Tracking"
              description="Track all management activities for audit and analytics purposes"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Individual management feature configuration component
const ManagementFeatureConfig = ({ featureType, config, formData, onInputChange, templateType }) => (
  <div className="space-y-6">
    <div className="flex items-center gap-3">
      <config.icon className="h-6 w-6 text-blue-600" />
      <h4 className="text-lg font-semibold">{config.label} Configuration</h4>
    </div>

    {/* Feature-specific settings */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <FormField label="Feature Name">
        <input
          type="text"
          value={formData.displayName || config.label}
          onChange={(e) => onInputChange('displayName', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </FormField>

      <FormField label="Access Level">
        <SelectInput
          value={formData.accessLevel || 'internal'}
          onChange={(value) => onInputChange('accessLevel', value)}
          options={[
            { value: 'public', label: 'Public Access' },
            { value: 'members', label: 'Members Only' },
            { value: 'internal', label: 'Internal Only' }
          ]}
        />
      </FormField>
    </div>

    {/* Feature-specific configurations */}
    {featureType === 'team' && (
      <div className="space-y-4">
        <Toggle
          checked={formData.enableDirectory || true}
          onChange={(value) => onInputChange('enableDirectory', value)}
          label="Enable Team Directory"
          description="Show public team directory"
        />
        <Toggle
          checked={formData.enableRoleManagement || false}
          onChange={(value) => onInputChange('enableRoleManagement', value)}
          label="Enable Role Management"
          description="Manage team roles and permissions"
        />
      </div>
    )}

    {featureType === 'assets' && (
      <div className="space-y-4">
        <Toggle
          checked={formData.enableMaintenanceTracking || true}
          onChange={(value) => onInputChange('enableMaintenanceTracking', value)}
          label="Enable Maintenance Tracking"
          description="Track asset maintenance schedules"
        />
        <Toggle
          checked={formData.enableQRCodes || false}
          onChange={(value) => onInputChange('enableQRCodes', value)}
          label="Enable QR Code Labels"
          description="Generate QR codes for asset tracking"
        />
      </div>
    )}

    {featureType === 'tasks' && (
      <div className="space-y-4">
        <Toggle
          checked={formData.enableKanbanBoard || true}
          onChange={(value) => onInputChange('enableKanbanBoard', value)}
          label="Enable Kanban Board"
          description="Visual task management with boards"
        />
        <Toggle
          checked={formData.enableTimeTracking || false}
          onChange={(value) => onInputChange('enableTimeTracking', value)}
          label="Enable Time Tracking"
          description="Track time spent on tasks"
        />
      </div>
    )}

    {featureType === 'sites' && (
      <div className="space-y-4">
        <Toggle
          checked={formData.enableCapacityTracking || true}
          onChange={(value) => onInputChange('enableCapacityTracking', value)}
          label="Enable Capacity Tracking"
          description="Track site capacity and occupancy"
        />
        <Toggle
          checked={formData.enableBookings || false}
          onChange={(value) => onInputChange('enableBookings', value)}
          label="Enable Site Bookings"
          description="Allow booking of sites and venues"
        />
      </div>
    )}

    {featureType === 'clients' && (
      <div className="space-y-4">
        <Toggle
          checked={formData.enableCRM || true}
          onChange={(value) => onInputChange('enableCRM', value)}
          label="Enable CRM Features"
          description="Customer relationship management tools"
        />
        <Toggle
          checked={formData.enableCommunications || false}
          onChange={(value) => onInputChange('enableCommunications', value)}
          label="Enable Communication Tracking"
          description="Track emails, calls, and interactions"
        />
      </div>
    )}

    {featureType === 'documents' && (
      <div className="space-y-4">
        <Toggle
          checked={formData.enableVersionControl || true}
          onChange={(value) => onInputChange('enableVersionControl', value)}
          label="Enable Version Control"
          description="Track document versions and revisions"
        />
        <Toggle
          checked={formData.enableApprovalWorkflow || false}
          onChange={(value) => onInputChange('enableApprovalWorkflow', value)}
          label="Enable Approval Workflows"
          description="Require approvals for document publishing"
        />
      </div>
    )}

    {featureType === 'resources' && (
      <div className="space-y-4">
        <Toggle
          checked={formData.enableAllocationTracking || true}
          onChange={(value) => onInputChange('enableAllocationTracking', value)}
          label="Enable Allocation Tracking"
          description="Track resource allocations across sites and people"
        />
        <Toggle
          checked={formData.enableQRCodeLabels || false}
          onChange={(value) => onInputChange('enableQRCodeLabels', value)}
          label="Enable QR Code Labels"
          description="Generate QR codes for resource tracking"
        />
        <Toggle
          checked={formData.enableMaintenanceScheduling || true}
          onChange={(value) => onInputChange('enableMaintenanceScheduling', value)}
          label="Enable Maintenance Scheduling"
          description="Schedule and track resource maintenance"
        />
      </div>
    )}

    {featureType === 'records' && (
      <div className="space-y-4">
        <FormField label="Activity Retention Period (days)">
          <SelectInput
            value={formData.retentionDays || '365'}
            onChange={(value) => onInputChange('retentionDays', value)}
            options={[
              { value: '30', label: '30 days' },
              { value: '90', label: '90 days' },
              { value: '180', label: '6 months' },
              { value: '365', label: '1 year' },
              { value: '1095', label: '3 years' },
              { value: '1825', label: '5 years' }
            ]}
          />
        </FormField>
        
        <Toggle
          checked={formData.enableRealTimeTracking || true}
          onChange={(value) => onInputChange('enableRealTimeTracking', value)}
          label="Enable Real-Time Tracking"
          description="Track activities as they happen across all modules"
        />
        
        <Toggle
          checked={formData.enableAnalytics || true}
          onChange={(value) => onInputChange('enableAnalytics', value)}
          label="Enable Analytics Dashboard"
          description="Visual analytics and insights from activity data"
        />
        
        <Toggle
          checked={formData.enableExportCapability || false}
          onChange={(value) => onInputChange('enableExportCapability', value)}
          label="Enable Export Capability"
          description="Allow exporting of activity records for compliance"
        />
      </div>
    )}

    <FormField label="Description">
      <textarea
        value={formData.description || ''}
        onChange={(e) => onInputChange('description', e.target.value)}
        placeholder={`Describe how ${config.label.toLowerCase()} will be used...`}
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
      />
    </FormField>
  </div>
);

export default ManagementSection;